/**
 * @file PdfViewerComponent.jsx
 * @description Render a PDF with a single chosen method. On the first failure,
 *              immediately show a download CTA (no fallback chain).
 *              When using "pdfjs-mozilla", the PDF is fetched via a proxy
 *              to avoid CORS issues, then displayed through the local viewer.html.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { getOptions } from '../../../Utils/Utils';

import './PdfViewerComponent.scss';

/** Helpers */
function isPdfContentType(ct) {
    const v = (ct || '').toLowerCase();
    return v.indexOf('application/pdf') !== -1;
}

async function looksLikePdf(blob) {
    try {
        if (!blob || !blob.size) return false;
        const head = await blob.slice(0, 5).arrayBuffer();
        const text = new TextDecoder('ascii').decode(new Uint8Array(head));
        return text.indexOf('%PDF-') === 0;
    } catch {
        return false;
    }
}

/**
 * Verify that the local PDF.js viewer page is reachable and matches
 * the expected build (no redirect, same URL, correct content-type,
 * and required HTML markers present).
 *
 * Returns: { ok: boolean, status?: number, finalUrl?: string, reason?: string }
 */
async function checkViewerHtmlReachable(
    url,
    { timeout = 6000, requireSameOrigin = true, requireSameUrl = true } = {}
) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    try {
        const requested = new URL(url, window.location.href);
        if (requireSameOrigin && requested.origin !== window.location.origin) {
            return { ok: false, reason: 'cross_origin_block', finalUrl: requested.href };
        }

        const res = await fetch(requested.href, {
            method: 'GET',
            cache: 'no-store',
            mode: requireSameOrigin ? 'same-origin' : 'cors',
            redirect: 'follow',
            credentials: 'same-origin',
            signal: controller.signal,
        });

        const finalUrl = res.url || requested.href;

        if (!res.ok) return { ok: false, reason: `http_${res.status}`, status: res.status, finalUrl };
        if (res.redirected) return { ok: false, reason: 'redirected', status: res.status, finalUrl };

        if (requireSameUrl) {
            const stripHash = u => {
                const x = new URL(u, window.location.href);
                x.hash = '';
                return x.href;
            };
            if (stripHash(finalUrl) !== stripHash(requested.href)) {
                return { ok: false, reason: 'url_mismatch', status: res.status, finalUrl };
            }
        }

        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (!ct.includes('text/html')) {
            return { ok: false, reason: 'not_html', status: res.status, finalUrl };
        }

        const html = await res.text();
        const requiredMarkers = [
            '<title>PDF.js viewer</title>',
            '<link rel="stylesheet" href="viewer.css">',
            '<script src="../build/pdf.mjs" type="module"></script>',
            '<script src="viewer.mjs" type="module"></script>',
            'id="outerContainer"',
            'id="viewerContainer"',
            'id="viewer" class="pdfViewer"',
            'id="toolbarViewer"',
            'id="sidebarContainer"',
            'id="loadingBar"',
            'id="printServiceDialog"',
        ];
        const missing = requiredMarkers.filter(sig => !html.includes(sig));
        if (missing.length) {
            return {
                ok: false,
                reason: `signature_missing:${missing.join('|')}`,
                status: res.status,
                finalUrl,
            };
        }

        return { ok: true, status: res.status, finalUrl };
    } catch (err) {
        return { ok: false, reason: err.name === 'AbortError' ? 'timeout' : (err.message || 'fetch_error') };
    } finally {
        clearTimeout(timer);
    }
}

/** Build axios options for proxy request (to avoid CORS when using pdfjs-mozilla). */
function buildProxyOptions(rawUrl, controller) {
    const proxyUrl = `/api/v2/sales-route/proxy/pdf?url=${encodeURIComponent(rawUrl)}`;
    const base = getOptions({ url: proxyUrl, method: 'get' }) || {};
    return {
        method: 'GET',
        url: base.url || proxyUrl,
        headers: base.headers || {},
        responseType: 'blob',
        signal: controller.signal,
        timeout: 15000,
        withCredentials: true,
    };
}

/**
 * Allowed render methods (pick ONE; no fallback).
 * - 'pdfjs-mozilla': local PDF.js viewer (/pdfjs/web/viewer.html + blob via proxy)
 * - 'object' | 'embed' | 'iframe' : native browser renderers (direct pdfUrl)
 * - 'pdfjs-google' : Google Docs Viewer (direct pdfUrl, must be publicly reachable)
 */
const PdfViewerComponent = ({
    pdfUrl,
    iframeWidth = '100%',
    iframeHeight = '801px',
    className = '',
    method = 'pdfjs-mozilla',
}) => {
    const [failed, setFailed] = useState(false);
    const [lastError, setLastError] = useState(null);

    // For pdfjs-mozilla
    const [viewerOk, setViewerOk] = useState(null); // null | true | false
    const [blobUrl, setBlobUrl] = useState(null);

    // Resolve viewer.html based on PUBLIC_URL (CRA-like)
    const PUBLIC_URL =
        (typeof process !== 'undefined' && process && process.env && process.env.PUBLIC_URL) || '';
    const viewerHtml = String(PUBLIC_URL).replace(/\/$/, '') + '/pdfjs/web/viewer.html';

    /** Strong reset when input changes */
    useEffect(() => {
        setFailed(false);
        setLastError(null);
        setViewerOk(null);
        if (blobUrl) {
            try { URL.revokeObjectURL(blobUrl); } catch { }
            setBlobUrl(null);
        }
    }, [method, pdfUrl]);

    /** Revoke blob on unmount */
    useEffect(() => {
        return () => {
            if (blobUrl) {
                try { URL.revokeObjectURL(blobUrl); } catch { }
            }
        };
    }, [blobUrl]);

    // ---- StrictMode-safe pipeline: check viewer + proxy prefetch → blob URL ----
    const runIdRef = useRef(0);
    const controllerRef = useRef(null);
    const pendingRef = useRef(false);

    useEffect(() => {
        if (failed || method !== 'pdfjs-mozilla' || !pdfUrl) return;

        const runId = ++runIdRef.current;
        const controller = new AbortController();
        controllerRef.current = controller;
        pendingRef.current = true;

        (async () => {
            try {
                // 1) Check local viewer
                const check = await checkViewerHtmlReachable(viewerHtml);
                if (runId !== runIdRef.current) return; // obsolete run
                setViewerOk(check.ok);
                if (!check.ok) {
                    setLastError('viewerHtml unreachable: ' + (check.reason || 'unknown'));
                    setFailed(true);
                    return;
                }

                // 2) Prefetch PDF via proxy → blob
                const axiosOptions = buildProxyOptions(pdfUrl, controller);
                const res = await axios(axiosOptions);
                if (runId !== runIdRef.current) return; // obsolete run

                const ct = res?.headers?.['content-type'] || '';
                const blob = res?.data;

                const headerOk = await looksLikePdf(blob);
                const typeOk = isPdfContentType(ct) || /octet-stream/i.test(ct);
                if (!headerOk && !typeOk) throw new Error(`Not a PDF (content-type="${ct}")`);

                const objectUrl = URL.createObjectURL(blob);
                setBlobUrl(prev => {
                    if (prev) { try { URL.revokeObjectURL(prev); } catch { } }
                    return objectUrl;
                });
            } catch (err) {
                const canceled =
                    (typeof axios.isCancel === 'function' && axios.isCancel(err)) ||
                    err?.code === 'ERR_CANCELED' ||
                    err?.name === 'AbortError';
                if (!canceled && runId === runIdRef.current) {
                    setLastError('prefetch/validation failed: ' + (err?.message || String(err)));
                    setFailed(true);
                }
            } finally {
                if (runId === runIdRef.current) pendingRef.current = false;
            }
        })();

        // Cleanup: abort only if this is the active run and still pending
        return () => {
            if (runId === runIdRef.current && pendingRef.current && controllerRef.current) {
                try { controllerRef.current.abort(); } catch { }
            }
        };
    }, [method, pdfUrl, viewerHtml, failed]);

    const commonProps = {
        width: iframeWidth,
        height: iframeHeight,
        title: 'PDF Viewer',
        onError: () => {
            setLastError('embed error from ' + method);
            setFailed(true);
        },
    };

    function renderFinalError() {
        if (!pdfUrl) return null;
        return (
            <div className="pdf-viewer__error">
                <p>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                        Télécharger la brochure au format PDF
                    </a>
                </p>
                {process && process.env && process.env.NODE_ENV !== 'production' && lastError ? (
                    <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap', opacity: 0.7 }}>
                        [debug] {String(lastError)}
                    </pre>
                ) : null}
            </div>
        );
    }

    if (!pdfUrl) return null;
    if (failed) return renderFinalError();

    // Render a single chosen method; onError → CTA
    switch (method) {
        case 'pdfjs-mozilla': {
            if (viewerOk === false) return renderFinalError();
            if (!blobUrl) {
                return (
                    <div className="pdf-viewer__loading">
                        <p>Chargement du document…</p>
                    </div>
                );
            }
            return (
                <iframe
                    src={viewerHtml + '?file=' + encodeURIComponent(blobUrl) + '#zoom=page-width'}
                    style={{ width: '100%', height: '100vh', border: 'none' }}
                    title="PDF.js Mozilla Viewer"
                    onError={() => {
                        setLastError('iframe error (pdfjs-mozilla)');
                        setFailed(true);
                    }}
                />
            );
        }

        case 'pdfjs-google':
            return (
                <iframe
                    src={'https://docs.google.com/gview?url=' + encodeURIComponent(pdfUrl) + '&embedded=true'}
                    style={{ width: '100%', height: '100vh', border: 'none' }}
                    title="Google Docs Viewer"
                    {...commonProps}
                />
            );

        case 'iframe':
            return <iframe src={pdfUrl} {...commonProps} />;

        case 'embed':
            return (
                <embed
                    src={pdfUrl}
                    type="application/pdf"
                    onError={e => {
                        try {
                            if (e?.target?.style) e.target.style.display = 'none';
                        } catch { }
                        commonProps.onError();
                    }}
                    {...commonProps}
                />
            );

        case 'object':
        default:
            return (
                <object data={pdfUrl} type="application/pdf" name="PDF Object" {...commonProps}>
                    {/* No automatic fallbacks here; direct CTA on error */}
                    {renderFinalError()}
                </object>
            );
    }
};

PdfViewerComponent.propTypes = {
    pdfUrl: PropTypes.string,
    iframeWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iframeHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    method: PropTypes.string,
};

export default PdfViewerComponent;
